import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCornerUpLeft, FiDownload, FiRefreshCw } from 'react-icons/fi';
import api from '../../libs/axios';

// DECISION-004 phase 2: add the balls kept aside from a phone that lost scoring, when they
// continue straight from the last ball on the server. Preview first; Apply and Undo need a
// reason (kept in the score edit history). Shows first names and a short phone tag only.

export interface SetAsideBallRow {
  log_id?: number;
  event_counter: number;
  inning_number: number;
  over_number: number | null;
  ball_number: number | null;
  ball_type: string | null;
  runs_scored: number | null;
  extras: number | null;
  is_wicket: boolean;
  copies: number;
  copies_differ: boolean;
  on_server: boolean;
}

export interface SetAsideGroupView {
  group_key: string;
  device_tag: string | null;
  name: string | null;
  user_id?: number | null;
  first_at: string;
  last_at: string;
  ball_count: number;
  balls: SetAsideBallRow[];
  fits: boolean;
  can_import: boolean;
  reason: string | null;
  message: string | null;
  apply_counters: number[];
  already_on_server: number;
}

export interface SetAsidePreview {
  server: { last_ball: { innings: number; over_label: string; runs: number; wickets: number } | null; server_max_counter: number; match_status: string };
  scoring: { holder_name: string | null; idle: boolean; reason: string | null; message: string | null };
  groups: SetAsideGroupView[];
  last_import: {
    import_id: string;
    at: string;
    counters: number[];
    undone: boolean;
    reason: string | null;
    can_undo: boolean;
    undo_message: string | null;
  } | null;
}

const ist = (iso: string | null | undefined) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export const setAsideBallLabel = (b: SetAsideBallRow): string => {
  const runs = b.runs_scored ?? 0;
  const t = (b.ball_type ?? 'legal').toLowerCase();
  let s: string;
  if (t === 'wide') s = (b.extras ?? 1) > 1 ? `Wd+${(b.extras ?? 1) - 1}` : 'Wd';
  else if (t === 'no_ball') s = runs > 0 ? `Nb+${runs}` : 'Nb';
  else if (t === 'bye') s = `${b.extras ?? runs}b`;
  else if (t === 'leg_bye') s = `${b.extras ?? runs}lb`;
  else s = String(runs);
  return b.is_wicket ? `${s} W` : s;
};

const overOf = (b: SetAsideBallRow) => (b.over_number != null && b.ball_number != null ? `${b.over_number}.${b.ball_number}` : `#${b.event_counter}`);

interface Props {
  matchId: string;
  onChanged?: () => void;
}

const SetAsideImportPanel: React.FC<Props> = ({ matchId, onChanged }) => {
  const [preview, setPreview] = useState<SetAsidePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await api.get(`/match-ops/${matchId}/set-aside`);
      setPreview(res.data as SetAsidePreview);
    } catch (e: any) {
      setPreview(null);
      setError(e?.response?.status === 404 ? 'This server does not support adding set-aside balls yet.' : (e?.response?.data?.message || 'Could not load the set-aside balls.'));
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  const reasonOk = reason.trim().length >= 5;
  const group = preview?.groups.find((g) => g.group_key === selected) ?? null;

  const apply = async () => {
    if (!group || !reasonOk || busy) return;
    setBusy(true);
    try {
      const res: any = await api.post(`/match-ops/${matchId}/set-aside/import`, {
        group_key: group.group_key,
        reason: reason.trim(),
        expect_counters: group.apply_counters,
      });
      toast.success(res?.message || 'Balls added');
      setReason('');
      setSelected(null);
      onChanged?.();
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The balls were not added.', { duration: 9000 });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!preview?.last_import || !reasonOk || busy) return;
    setBusy(true);
    try {
      const res: any = await api.post(`/match-ops/${matchId}/set-aside/undo-import`, { reason: reason.trim() });
      toast.success(res?.message || 'Import undone');
      setReason('');
      onChanged?.();
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The import was not undone.', { duration: 9000 });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid="set-aside-import">
      <div className="flex items-center gap-2">
        <FiDownload className="w-4 h-4 text-primary-600" />
        <h4 className="text-sm font-semibold text-gray-900">Add set-aside balls to the match</h4>
        <button onClick={load} disabled={loading || busy} className="ml-auto text-xs text-primary-700 hover:underline disabled:opacity-50 flex items-center gap-1">
          <FiRefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Only balls that continue straight from the last ball on the server can be added, all of one phone&apos;s balls together, and only while nobody is scoring.
        Anything else is entered from the scorebook.
      </p>

      {loading && !preview && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {preview && (
        <>
          <p className="text-xs text-gray-600">
            Server now: {preview.server.last_ball ? `innings ${preview.server.last_ball.innings}, ${preview.server.last_ball.runs}/${preview.server.last_ball.wickets} after ${preview.server.last_ball.over_label} overs` : '-'}
            {` · last ball #${preview.server.server_max_counter}`}
            {!preview.scoring.idle && preview.scoring.message ? ` · ${preview.scoring.message}` : ''}
          </p>

          {preview.groups.length === 0 ? <p className="text-xs text-gray-400">No set-aside balls waiting.</p> : (
            <div className="space-y-2">
              {preview.groups.map((g) => (
                <label key={g.group_key} className={`block rounded-lg border p-3 text-sm cursor-pointer ${selected === g.group_key ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                  <div className="flex items-start gap-2">
                    <input type="radio" name="set-aside-group" className="mt-1" checked={selected === g.group_key}
                      onChange={() => setSelected(g.group_key)} disabled={busy} />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {g.name ? `${g.name}'s phone` : 'A phone'}{g.device_tag ? ` (${g.device_tag})` : ''}{g.user_id != null ? ` · #${g.user_id}` : ''}
                        {` · ${g.ball_count} ball${g.ball_count === 1 ? '' : 's'}`}
                      </div>
                      <div className="text-xs text-gray-500">{ist(g.first_at)} – {ist(g.last_at)}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {g.balls.map((b) => (
                          <span key={b.event_counter} title={`Ball #${b.event_counter}${b.copies_differ ? ' · the phone sent different versions; the latest is used' : ''}`}
                            className={`px-1.5 py-0.5 rounded text-xs ${b.on_server ? 'bg-gray-100 text-gray-400 line-through' : g.apply_counters.includes(b.event_counter) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                            {overOf(b)} {setAsideBallLabel(b)}{b.copies_differ ? '*' : ''}
                          </span>
                        ))}
                      </div>
                      <p className={`text-xs mt-1 ${g.can_import ? 'text-green-700' : 'text-amber-700'}`}>
                        {g.can_import
                          ? `${g.apply_counters.length} ball${g.apply_counters.length === 1 ? '' : 's'} can be added after ball #${preview.server.server_max_counter}${g.already_on_server ? ` (${g.already_on_server} already on the server)` : ''}.`
                          : (g.message || 'These balls can\'t be added.')}
                      </p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {preview.last_import && !preview.last_import.undone && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700">
              Last import {ist(preview.last_import.at)}: balls #{preview.last_import.counters[0]}–#{preview.last_import.counters[preview.last_import.counters.length - 1]}
              {preview.last_import.reason ? ` · "${preview.last_import.reason}"` : ''}
              {!preview.last_import.can_undo && preview.last_import.undo_message ? ` · ${preview.last_import.undo_message}` : ''}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-900 mb-1" htmlFor="set-aside-reason">Reason <span className="font-normal text-gray-500">(kept in the edit history)</span></label>
            <textarea id="set-aside-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} rows={2} disabled={busy}
              placeholder="e.g. Ravi's phone died at 12.3; organiser confirmed these balls from the scorebook"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </div>

          <div className="flex justify-end gap-3">
            {preview.last_import && !preview.last_import.undone && (
              <button onClick={undo} disabled={busy || !reasonOk || !preview.last_import.can_undo}
                className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">
                <FiCornerUpLeft className="w-4 h-4" /> Undo last import
              </button>
            )}
            <button onClick={apply} disabled={busy || !reasonOk || !group || !group.can_import}
              className="px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? 'Working…' : group ? `Add ${group.apply_counters.length} ball${group.apply_counters.length === 1 ? '' : 's'}` : 'Add balls'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SetAsideImportPanel;
